/**
 * A presentation layer for the existing WordPress YouTube embed.
 * The native player and authored caption remain the no-JS/error fallback.
 */
export class ArticleVideo {
  constructor(scope) {
    this.scope = scope;
    this.listeners = [];
    this.player = scope.querySelector('.amt-article-video__player');
    this.iframe = this.player?.querySelector('iframe');
    if (!this.iframe || scope.classList.contains('is-enhanced')) return;

    // The consent integration serves about:blank even to returning visitors.
    // Keep the authored URL without contacting YouTube until consent is saved.
    this.consentService = this.iframe.getAttribute('data-amt-consent-service');
    this.consentManaged = Boolean(this.consentService) || this.iframe.hasAttribute('data-cookiecare-category');
    this.originalSource = this.iframe.getAttribute('data-amt-consent-src') || this.iframe.getAttribute('data-cookiecare-src') || this.iframe.getAttribute('src');
    let source;
    try {
      source = new URL(this.originalSource, scope.ownerDocument.baseURI);
    } catch (error) {
      return;
    }
    const allowedHosts = ['youtube.com', 'www.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'];
    const match = source.pathname.match(/^\/embed\/([A-Za-z0-9_-]{11})\/?$/);
    if (!['https:', 'http:'].includes(source.protocol) || !allowedHosts.includes(source.hostname) || !match) return;

    this.source = source;
    this.originalHidden = this.iframe.hidden;
    this.originalTabindex = this.iframe.getAttribute('tabindex');
    this.caption = scope.querySelector('.amt-article-video__caption');
    const french = scope.ownerDocument.documentElement.lang.toLowerCase().startsWith('fr');
    this.playLabel = french ? 'Lire la vidéo' : 'Play video';
    const title = (this.iframe.getAttribute('title') || '').trim();
    const genericTitle = !title || /^(?:(?:youtube\s+)?video(?:\s+player)?|embedded\s+video|lecteur\s+vid[ée]o(?:\s+youtube)?|youtube)$/i.test(title);
    this.title = genericTitle ? '' : title;
    this.captionLabel = this.caption?.textContent.trim() || '';

    this.image = scope.ownerDocument.createElement('img');
    this.image.alt = '';
    this.image.width = 480;
    this.image.height = 360;
    this.image.decoding = 'async';
    this.on(this.image, 'load', () => this.enhance());
    this.on(this.image, 'error', () => {
      if (this.playing) return;
      const restoreFocus = this.preview?.contains(scope.ownerDocument.activeElement);
      this.fallback();
      if (restoreFocus && this.hasConsent()) this.iframe.focus({ preventScroll: true });
    });
    // A visitor may start using the native player while the preview is still
    // loading. Do not hide an already focused/playing embed when it arrives.
    this.on(this.iframe, 'focus', () => {
      if (!this.enhanced) this.fallback();
    });
    this.posterSource = `https://i.ytimg.com/vi/${match[1]}/hqdefault.jpg`;
    // Capture runs before the native unblocker's normal listener removes its
    // focused Enable button after the asynchronous consent request succeeds.
    if (this.consentManaged) {
      const sync = () => this.syncConsent();
      scope.ownerDocument.addEventListener('cookiecare:consent', sync, true);
      scope.ownerDocument.addEventListener('amt:consent', sync, true);
      this.removeConsentListener = () => {
        scope.ownerDocument.removeEventListener('cookiecare:consent', sync, true);
        scope.ownerDocument.removeEventListener('amt:consent', sync, true);
      };
    }
    this.syncConsent();
  }

  hasConsent() {
    if (this.consentService) return this.scope.ownerDocument.defaultView.AftermetooConsent?.hasConsent(this.consentService) === true;
    return !this.consentManaged || this.scope.ownerDocument.defaultView.CookieCare?.hasConsent('functional') === true;
  }

  syncConsent() {
    if (this.destroyed) return;
    if (!this.hasConsent()) {
      this.resetPresentation();
      this.image.removeAttribute('src');
      if (this.iframe.getAttribute('src') !== 'about:blank') this.iframe.setAttribute('src', 'about:blank');
      return;
    }
    const placeholder = this.iframe.previousElementSibling;
    const restoreFocus = (placeholder?.hasAttribute('data-cookiecare-placeholder') || placeholder?.hasAttribute('data-amt-consent-placeholder'))
      && placeholder.contains(this.scope.ownerDocument.activeElement);
    // The native unblocker may run before or after this event handler.
    if (this.consentManaged && this.iframe.getAttribute('src') === 'about:blank') this.iframe.setAttribute('src', this.originalSource);
    if (this.fallbackMode) {
      if (restoreFocus) this.iframe.focus({ preventScroll: true });
      return;
    }
    if (!this.image.getAttribute('src')) this.image.src = this.posterSource;
    if (restoreFocus) {
      // Reserve the existing poster layout while its image loads, so keyboard
      // focus has an immediate destination and the iframe is not hidden later
      // after a visitor has started interacting with its native controls.
      this.enhance(true);
      (this.enhanced ? this.preview : this.iframe).focus({ preventScroll: true });
    }
  }

  on(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    this.listeners.push(() => target.removeEventListener(event, handler, options));
  }

  enhance(reservePoster = false) {
    if (this.destroyed || this.fallbackMode || this.enhanced || (!this.image.naturalWidth && !reservePoster) || !this.hasConsent()) return;
    const doc = this.scope.ownerDocument;
    if (doc.activeElement === this.iframe) {
      this.fallback();
      return;
    }
    const label = this.title || this.captionLabel;
    const accessibleLabel = label ? `${this.playLabel}: ${label}` : this.playLabel;

    this.preview = doc.createElement('button');
    this.preview.type = 'button';
    this.preview.className = 'amt-article-video__preview';
    this.preview.setAttribute('aria-label', accessibleLabel);
    this.preview.append(this.image);
    this.player.append(this.preview);

    if (!this.caption) {
      this.caption = doc.createElement('figcaption');
      this.caption.className = 'col-12 col-md amt-article-video__caption';
      this.scope.append(this.caption);
      this.createdCaption = true;
    }
    this.description = doc.createElement('div');
    this.description.className = 'amt-article-video__description';
    this.description.append(...this.caption.childNodes);

    this.action = doc.createElement('button');
    this.action.type = 'button';
    this.action.className = 'amt-article-video__action';
    this.action.setAttribute('aria-label', accessibleLabel);
    const icon = doc.createElement('span');
    icon.className = 'amt-article-video__icon';
    icon.setAttribute('aria-hidden', 'true');
    this.action.append(icon);

    // Generic oEmbed titles are not editorial copy. In that case the original
    // caption stays visible once, and also labels both play controls.
    if (this.title || !this.captionLabel) {
      this.titleElement = doc.createElement('span');
      this.titleElement.className = 'amt-article-video__title';
      this.titleElement.textContent = this.title || this.playLabel;
      this.action.append(this.titleElement);
    }
    this.caption.append(this.action, this.description);
    this.on(this.preview, 'click', () => this.play());
    this.on(this.action, 'click', () => this.play());
    this.iframe.hidden = true;
    this.scope.classList.add('is-enhanced');
    this.enhanced = true;
  }

  play() {
    if (this.destroyed || this.fallbackMode || this.playing) return;
    if (!this.hasConsent()) {
      if (this.consentService) this.scope.ownerDocument.defaultView.AftermetooConsent?.openSettings();
      else this.scope.ownerDocument.dispatchEvent(new CustomEvent('cookiecare:request'));
      return;
    }
    this.playing = true;
    const playingSource = new URL(this.source.href);
    playingSource.searchParams.set('autoplay', '1');
    this.iframe.setAttribute('src', playingSource.href);
    this.preview.hidden = true;
    this.iframe.hidden = false;
    this.scope.classList.add('is-playing');

    // Native YouTube controls own playback from here; do not present a fake
    // pause button whose state could diverge from the cross-origin player.
    this.playingTitle = this.scope.ownerDocument.createElement('div');
    this.playingTitle.className = 'amt-article-video__action';
    if (this.titleElement) this.playingTitle.append(this.titleElement);
    this.action.replaceWith(this.playingTitle);
    this.iframe.setAttribute('tabindex', '0');
    this.iframe.focus({ preventScroll: true });
  }

  destroy() {
    this.destroyed = true;
    this.fallback();
    this.removeConsentListener?.();
  }

  // Poster failure or a visitor choosing the native player ends enhancement,
  // but the native iframe must still be stopped when consent is withdrawn.
  fallback() {
    this.fallbackMode = true;
    this.listeners.forEach((off) => off());
    this.listeners = [];
    this.resetPresentation();
  }

  resetPresentation() {
    if (!this.enhanced) return;
    this.preview.remove();
    this.action.remove();
    this.playingTitle?.remove();
    this.description.replaceWith(...this.description.childNodes);
    if (this.createdCaption) { this.caption.remove(); this.caption = null; this.createdCaption = false; }
    this.scope.classList.remove('is-enhanced', 'is-playing');
    this.iframe.hidden = this.originalHidden;
    if (this.originalTabindex === null) this.iframe.removeAttribute('tabindex');
    else this.iframe.setAttribute('tabindex', this.originalTabindex);
    if (this.playing) this.iframe.setAttribute('src', this.hasConsent() ? this.originalSource : 'about:blank');
    this.playing = false;
    this.playingTitle = null;
    this.titleElement = null;
    this.enhanced = false;
  }
}
